import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

export interface ShowPeriodSummary {
  advanced: true;
  eventId: string;
  revision: number;
  timezone: string;
  betweenShows: boolean;
  periods: {
    periodId: string; position: number; label: string; showDate: string;
    showStartAt: string; showEndAt: string; pickupMode: 'delivery' | 'customer_pickup';
    prepAt: string; shipAt: string; setupAt: string; teardownAt: string; returnAt: string;
    requiredQuantity: number; activeQuantity: number; missingQuantity: number; complete: boolean;
  }[];
}
export type EventShowPeriodSummary = { advanced: false } | ShowPeriodSummary;
export const showPeriodSummaryQueryKey = (eventIds: string[]) => ['event-show-period-summaries', ...[...new Set(eventIds)].sort()] as const;
const invalid = () => new Error('Show-period status could not be verified. Refresh to load its current state.');
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid();
  return value as Record<string, unknown>;
}
function text(value: unknown): string { if (typeof value !== 'string' || !value) throw invalid(); return value; }
function count(value: unknown): number {
  if ((typeof value !== 'string' && typeof value !== 'number') || value === '') throw invalid();
  const number = Number(value); if (!Number.isSafeInteger(number) || number < 0) throw invalid(); return number;
}
function bool(value: unknown): boolean { if (typeof value !== 'boolean') throw invalid(); return value; }
function time(value: unknown): string {
  const result = text(value);
  if (!Number.isFinite(Date.parse(result)) || !/(Z|[+-]\d{2}:?\d{2})$/.test(result)) throw invalid(); return result;
}
export function parseShowPeriodSummaries(value: unknown, eventIds: string[]): Record<string, EventShowPeriodSummary> {
  const data = object(value), requested = [...new Set(eventIds)], result: Record<string, EventShowPeriodSummary> = {};
  if (Object.keys(data).length !== requested.length || Object.keys(data).some(key => !requested.includes(key))) throw invalid();
  for (const eventId of requested) {
    const row = object(data[eventId]);
    if (row.advanced === false) { result[eventId] = { advanced: false }; continue; }
    if (row.advanced !== true || row.eventId !== eventId || count(row.revision) < 1 || !Array.isArray(row.periods) || !row.periods.length) throw invalid();
    const timezone = text(row.timezone);
    try { new Intl.DateTimeFormat('en-US', { timeZone: timezone }); } catch { throw invalid(); }
    const periods = row.periods.map(value => {
      const period = object(value), position = count(period.position), showDate = text(period.showDate);
      if (position < 1 || !['delivery', 'customer_pickup'].includes(String(period.pickupMode)) || !/^\d{4}-\d{2}-\d{2}$/.test(showDate)
        || period.label !== null && typeof period.label !== 'string') throw invalid();
      return { periodId: text(period.periodId), position, label: typeof period.label === 'string' ? period.label : '', showDate,
        prepAt: time(period.prepAt), shipAt: time(period.shipAt), setupAt: time(period.setupAt),
        teardownAt: time(period.teardownAt), returnAt: time(period.returnAt),
        showStartAt: time(period.showStartAt), showEndAt: time(period.showEndAt), pickupMode: period.pickupMode as 'delivery' | 'customer_pickup',
        requiredQuantity: count(period.requiredQuantity), activeQuantity: count(period.activeQuantity), missingQuantity: count(period.missingQuantity), complete: bool(period.complete) };
    });
    if (new Set(periods.map(period => period.periodId)).size !== periods.length || new Set(periods.map(period => period.position)).size !== periods.length) throw invalid();
    result[eventId] = { advanced: true, eventId, timezone, revision: count(row.revision), betweenShows: bool(row.betweenShows), periods };
  }
  return result;
}
export async function fetchShowPeriodSummaries(eventIds: string[], options?: { supabase?: SupabaseClient }): Promise<Record<string, EventShowPeriodSummary>> {
  const ids = [...new Set(eventIds)];
  if (!ids.length) return {};
  if (ids.length > 100 || ids.some(id => !id)) throw new Error('Select up to 100 events for a show-period status request.');
  const { data, error } = await (options?.supabase ?? getSupabaseClient()).rpc('get_event_show_period_summaries', { p_event_ids: ids });
  if (error) throw new Error(error.code === '42501' ? 'You do not have access to these event show periods.' : 'Show-period status could not be loaded. Refresh to retry.');
  return parseShowPeriodSummaries(data, ids);
}
